import React from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import moment from "moment";

import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Icon
} from "@material-ui/core";

import { ActionPermissionsObject, IStripeInvoiceExtended, IStripeSubscriptionExtended, PermissionActionType } from "common/interfaces";
import BillingController from "js/controllers/BillingController";
import WorkspaceController from "js/controllers/WorkspaceController";
import { app } from "js/namespaces";
import { dialogTheme } from "js/react/materialThemeOverrides";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";

const Container = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1200;
    background: transparent;
    backdrop-filter: blur(5px);
`;

const AlertContainer = styled.div`
    color: red;
    font-size: 15px;
    text-transform: uppercase;
    display: inline-flex;
    align-items: center;

    >div {
        margin-left: 10px;
    }
`;

const Header = styled.div`
    margin-top: 20px;
`;

const StyledDialogContent = styled(DialogContent)`
    &&& {
        padding: 0 34px 20px;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    &&& {
        padding: 0 34px 20px;
    }
`;

interface EnterpriseWorkspaceBlockedDialogProps {
    workspaceId: string;
    isPastDue: boolean;
    forceLockWorkspace: boolean;
    contactEmail: string;
    isCanceled: boolean;
    onClose: () => void;
}

function EnterpriseWorkspaceBlockedDialog({ workspaceId, isPastDue, forceLockWorkspace, contactEmail, isCanceled, onClose }: EnterpriseWorkspaceBlockedDialogProps) {
    const { t } = useTranslation();
    const canManageBilling = WorkspaceController.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE];

    let subscription: IStripeSubscriptionExtended, invoice: IStripeInvoiceExtended;
    if (canManageBilling) {
        subscription = BillingController.subscription;
        invoice = subscription?.latest_invoice;
    }

    const daysUntilDowngrade = subscription
        ? moment.unix(subscription.current_period_start)
            .add(365, "days")
            .diff(moment(), "days")
        : null;

    const handleSwitchToAnotherWorkspace = () => {
        // Have to reference appController off app because it may be legacy or non-legacy controller
        const workspaces = app.appController.workspacesMetadata;
        const workspace = workspaces.find(workspace => workspace.id !== workspaceId);
        app.appController.switchWorkspace(workspace.id);
        app.appController.showLibrary();
        onClose();
    };

    const email = contactEmail ?? "support@beautiful.ai";

    return (
        <Container>
            <MuiThemeProvider theme={dialogTheme}>
                <BeautifulDialog maxWidth="md" fullWidth={false} hideBackdrop closeDialog={() => { }}>
                    <DialogTitle>
                        <AlertContainer>
                            <Icon>error</Icon>
                            <div>{t("Your workspace is blocked")}</div>
                        </AlertContainer>
                        {forceLockWorkspace && <Header>{t("Contact your account manager to unlock this workspace")}</Header>}
                        {!forceLockWorkspace && <>
                            {isPastDue && <Header>{t("Please pay your invoice to unlock this workspace")}</Header>}
                            {isCanceled && <Header>{t("Your subscription has been canceled")}</Header>}
                        </>}
                    </DialogTitle>
                    {isPastDue && <StyledDialogContent>
                        {invoice && <DialogContentText>
                            {t("The invoice from {{date}} hasn't been paid yet.", {
                                date: moment.unix(invoice.created).format("MM/DD/YYYY")
                            })}
                        </DialogContentText>}
                        {daysUntilDowngrade && <DialogContentText>
                            <Trans
                                t={t}
                                i18nKey="Your workspace will be removed in <bold>{{days}} days</bold>."
                                components={{ bold: <b /> }}
                                values={{ days: daysUntilDowngrade }}
                            />
                        </DialogContentText>}
                        {!daysUntilDowngrade && <DialogContentText>
                            {t("Your workspace will be removed because your payment has failed.")}
                        </DialogContentText>}
                        {canManageBilling && invoice && invoice.hosted_invoice_url && <DialogContentText>
                            <Trans
                                t={t}
                                i18nKey="You can view and pay the invoice using this <invoiceLink>link</invoiceLink>."
                                components={{
                                    invoiceLink: <a href={invoice.hosted_invoice_url} target="_blank" />
                                }}
                            />
                        </DialogContentText>}
                        <DialogContentText>
                            <Trans
                                t={t}
                                i18nKey="If you have questions, please contact <emailLink>{{email}}</emailLink>."
                                components={{
                                    emailLink: <a href={`mailto:${email}`} />
                                }}
                                values={{ email }}
                            />
                        </DialogContentText>
                    </StyledDialogContent>}
                    {!isPastDue && <StyledDialogContent>
                        <DialogContentText>
                            <Trans
                                t={t}
                                i18nKey="Your workspace is currently locked. Please contact <emailLink>{{email}}</emailLink> for assistance."
                                components={{
                                    emailLink: <a href={`mailto:${email}`} />
                                }}
                                values={{ email }}
                            />
                        </DialogContentText>
                    </StyledDialogContent>}
                    <StyledDialogActions>
                        <Button
                            color="primary"
                            onClick={handleSwitchToAnotherWorkspace}>
                            {t("Switch to another workspace")}
                        </Button>
                    </StyledDialogActions>
                </BeautifulDialog>
            </MuiThemeProvider>
        </Container >
    );
}

export default EnterpriseWorkspaceBlockedDialog;
